/**
 * The legacy react-router tree.
 *
 * Extracted from App.jsx so TanStack Router can own the root while this whole
 * tree renders underneath a catch-all route. This is a move, not a rewrite:
 * routes, guards and Faro wiring are unchanged.
 *
 * It lives in its own module because App.jsx imports the router, the router
 * imports the generated route tree, and that tree imports this file — keeping
 * it in App.jsx would be an import cycle.
 *
 * Screens leave here one at a time: add a real file-based route under
 * src/routes/ and delete the matching <Route> below.
 *
 * NOTE: react-router cannot be dropped from the client entirely while
 * omniflow-ui is embedded. That package has 85 files using react-router and
 * declares react-router-dom as a peerDependency, and its <Omniflow> export
 * renders OmniflowBuilder without its own RouterProvider — so components deep in
 * the flow builder resolve react-router hooks against THIS tree.
 */
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  createRoutesFromChildren,
  matchRoutes,
  useNavigationType,
} from "react-router-dom";

import { usePermissions } from "./hooks/usePermissions";
import AppRefreshProvider from "./components/AppRefreshProvider";
import { LegacyProtectedRoute as ProtectedRoute } from "./components/auth/LegacyProtectedRoute";

// Pages
import Login from "./pages/Login";
import LogoutCallback from "./pages/LogoutCallback";
import MoyasarPaymentCallback from "./pages/MoyasarPaymentCallback";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Deployments from "./pages/Deployments";
import DeploymentDetail from "./pages/DeploymentDetail";
import CreateDeployment from "./pages/CreateDeployment";
import PredefinedModels from "./pages/PredefinedModels";
import Flows from "./pages/Flows";
import TemplateDetail from "./pages/TemplateDetail";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import DocumentCollectionsPage from "./pages/DocumentCollectionsPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import ChatPlayground from "./pages/ChatPlayground";
import ChatStandalone from "./pages/ChatStandalone";
import AICatalog from "./pages/AICatalog";
import { Playground } from "./pages/Playground/Playground";
import ThemePage from "./pages/Theme/index";

import {
  createReactRouterV6Options,
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  WebVitalsInstrumentation,
} from "@grafana/faro-react";
import { FaroRoutes } from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";

// Silent SSO check page for Keycloak
const SilentCheckSSO = () => null;

const PermissionRoute = ({ allowed, children }) =>
  allowed ? children : <Navigate to="/" replace />;

const DeploymentRoute = ({ children }) => {
  const { canViewDeployments } = usePermissions();
  return (
    <PermissionRoute allowed={canViewDeployments}>{children}</PermissionRoute>
  );
};

const HideInferenceOnlyRoute = ({ children }) => {
  const { isInferenceOnly } = usePermissions();
  return (
    <PermissionRoute allowed={!isInferenceOnly}>{children}</PermissionRoute>
  );
};

const observabilityEnabled =
  window.env?.OBSERVABILITY_ENABLED === "true" ||
  window.env?.OBSERVABILITY_ENABLED === true;

if (observabilityEnabled) {
  const collectorEndpoint = window.env?.FARO_COLLECTOR_ENDPOINT;
  if (!collectorEndpoint) {
    console.warn("Faro enabled but FARO_COLLECTOR_ENDPOINT is missing.");
  } else {
    const url = collectorEndpoint.endsWith("/collect")
      ? collectorEndpoint
      : `${collectorEndpoint}/collect`;

    initializeFaro({
      url,
      app: {
        name: window.env?.FARO_APP_NAME,
        version: window.env?.FARO_APP_VERSION,
        environment: window.env?.FARO_APP_ENVIRONMENT,
      },
      apiKey: window.env?.FARO_API_KEY,
      instrumentations: [
        // Load the default Web instrumentations
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
        new ReactIntegration({
          router: createReactRouterV6Options({
            createRoutesFromChildren,
            matchRoutes,
            Routes,
            useLocation,
            useNavigationType,
          }),
        }),
        new WebVitalsInstrumentation(),
      ],
      webVitalsInstrumentation: {
        reportAllChanges: true,
      },
      sessionTracking: {
        samplingRate: Number(window.env?.FARO_SAMPLING_RATE || 1),
      },
    });
  }
}
const RoutesWrapper = observabilityEnabled ? FaroRoutes : Routes;

const LegacyRoutes = () => (
        <Router>
          <AppRefreshProvider>
            <RoutesWrapper>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route
                path="/logout-callback"
                element={<LogoutCallback />}
              />
              <Route
                path="/payment/moyasar/callback"
                element={<MoyasarPaymentCallback />}
              />
              <Route path="/register" element={<Register />} />
              <Route
                path="/silent-check-sso.html"
                element={<SilentCheckSSO />}
              />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deployments"
                element={
                  <ProtectedRoute>
                    <DeploymentRoute>
                      <Deployments />
                    </DeploymentRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deployments/:id"
                element={
                  <ProtectedRoute>
                    <DeploymentRoute>
                      <DeploymentDetail />
                    </DeploymentRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-deployment"
                element={
                  <ProtectedRoute>
                    <DeploymentRoute>
                      <CreateDeployment />
                    </DeploymentRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/predefined-models"
                element={
                  <ProtectedRoute>
                    <DeploymentRoute>
                      <PredefinedModels />
                    </DeploymentRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/update/theme/:organization_id"
                element={
                  <ProtectedRoute>
                    <HideInferenceOnlyRoute>
                      <ThemePage />
                    </HideInferenceOnlyRoute>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/collections"
                element={
                  <ProtectedRoute>
                    <HideInferenceOnlyRoute>
                      <DocumentCollectionsPage />
                    </HideInferenceOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/collections/:collectionId"
                element={
                  <ProtectedRoute>
                    <HideInferenceOnlyRoute>
                      <CollectionDetailPage />
                    </HideInferenceOnlyRoute>
                  </ProtectedRoute>
                }
              />
              {/* Legacy Playground routes are intentionally disabled so
              users cannot open the old Playground through a direct URL. */}
              {false && (
                <>
                  <Route
                    path="/legacyplayground"
                    element={
                      <ProtectedRoute>
                        <ChatPlayground />
                        {/* <Playground />*/}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/legacyplayground-standalone"
                    element={
                      <ProtectedRoute>
                        <ChatStandalone />
                        {/* <CollectionDetailPage /> */}

                        {/* <Playground singleRenderedChildren />*/}
                      </ProtectedRoute>
                    }
                  />
                </>
              )}
              <Route
                path="/chat-standalone/:id?"
                element={
                  <ProtectedRoute>
                    {/* <ChatStandalone /> */}
                    {/* <CollectionDetailPage /> */}

                    <Playground singleRenderedChildren />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/old-ai-catalog"
                element={
                  <ProtectedRoute>
                    <AICatalog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows"
                element={
                  <ProtectedRoute>
                    <HideInferenceOnlyRoute>
                      <Flows />
                    </HideInferenceOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/:ProjectId/:flowId"
                element={
                  <ProtectedRoute>
                    <HideInferenceOnlyRoute>
                      <Flows />
                    </HideInferenceOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/explore/:templateId"
                element={
                  <ProtectedRoute>
                    <HideInferenceOnlyRoute>
                      <TemplateDetail />
                    </HideInferenceOnlyRoute>
                  </ProtectedRoute>
                }
              />

              {/* Debug/test route */}
              <Route
                path="/test"
                element={<div>Test Route Works!</div>}
              />

              {/* Fallback 404 */}
              <Route path="*" element={<NotFound />} />
            </RoutesWrapper>
          </AppRefreshProvider>
        </Router>
);

export default LegacyRoutes;
